/**
 * Sealed product picker — modeled on quickCardLookup.
 *
 * Queries the local sealed_products + sealed_current_prices tables (synced
 * daily from TCGCSV). On pick, fills the sealed form's Product / Set / Image
 * URL / Current FMV / Valuation Source / tcgplayer_product_id fields.
 *
 * Mounts into #pickerRoot so it stacks above the Sealed edit modal.
 */
import { normalizeSetName } from "../lib/setNames";
import { CATEGORY_TO_GAME } from "../lib/catalogResolve";
import { escapeHtml, toast } from "../lib/dom";
import { fmt$ } from "../utils/format";
import { markModalDirty } from "./modal";
import { searchSealedProducts, isCatalogPopulated, type SealedProductRow } from "../lib/sealedTcgcsv";
import { trendButtonHtml, toggleCatalogTrend, isTrendClick } from "./catalogTrend";

let _rows: SealedProductRow[] = [];

function renderSubModal(title: string, body: string, footer: string, wide = true): void {
  let root = document.getElementById("pickerRoot");
  if (!root) { root = document.createElement("div"); root.id = "pickerRoot"; document.body.appendChild(root); }
  root.innerHTML = `
    <div class="fixed inset-0 modal-backdrop flex items-start justify-center p-3 sm:p-6 overflow-y-auto" style="z-index:55">
      <div class="panel w-full ${wide ? "max-w-5xl" : "max-w-xl"} my-6 shadow-2xl">
        <div class="flex items-center justify-between p-4 border-b border-[var(--border)]">
          <h3 class="font-semibold">${escapeHtml(title)}</h3>
          <button class="btn btn-ghost !py-1 !px-2" id="_spClose" title="Back to form">✕</button>
        </div>
        <div class="p-4">${body}</div>
        ${footer ? `<div class="p-4 border-t border-[var(--border)] flex justify-end gap-2">${footer}</div>` : ""}
      </div>
    </div>`;
  document.getElementById("_spClose")?.addEventListener("click", closeSealedPicker);
}

export function closeSealedPicker(): void {
  document.getElementById("pickerRoot") && (document.getElementById("pickerRoot")!.innerHTML = "");
}

/** Open the sealed picker. Optional searchTerm seeds the input — falls back to
 *  the sealed form's Product field if blank. */
export async function openSealedPicker(searchTerm?: string): Promise<void> {
  const form = document.getElementById("sealedForm") as HTMLFormElement | null;
  const seed = (searchTerm
    || (form ? (form.querySelector('[name="Product"]') as HTMLInputElement)?.value || "" : "")
  ).trim();

  // Seed from the form's Set field too, if any — used as an extra filter hint
  // shown in the help text.
  const setHint = form ? (form.querySelector('.combobox-wrapper[data-cbx="Set"] input') as HTMLInputElement | null)?.value || "" : "";

  // Render the loading state immediately so the user gets feedback
  renderSubModal(
    "Pick a sealed product — searching…",
    `<div class="space-y-3 mb-3">
      <div class="flex flex-wrap gap-2">
        <input type="text" id="sealedPickerRefine" class="input flex-1 min-w-[240px]"
               placeholder='e.g. "surging sparks etb" or "151 booster box"'
               value="${escapeHtml(seed).replace(/'/g,"&#39;")}" disabled>
        <button class="btn btn-primary" disabled>Searching…</button>
      </div>
    </div>
    <div class="py-16 text-center">
      <div class="inline-flex items-center gap-3 text-[var(--muted)]">
        <div class="w-6 h-6 border-2 rounded-full animate-spin" style="border-color:#6366f1; border-top-color:transparent"></div>
        <div>Searching catalog…</div>
      </div>
    </div>`,
    `<button class="btn btn-ghost" id="_spCancel">Cancel</button>`
  );
  document.getElementById("_spCancel")?.addEventListener("click", closeSealedPicker);

  // Catalog-not-populated guard
  if (!(await isCatalogPopulated())) {
    renderSubModal(
      "Sealed catalog not synced yet",
      `<div class="space-y-3 text-sm">
        <p>The sealed product catalog hasn't been imported from TCGCSV yet. Run the one-time backfill described in <code>TCGCSV-DEPLOY.md</code>:</p>
        <pre class="panel-2 p-3 text-xs overflow-x-auto"><code>curl -X POST \\
  -H "Authorization: Bearer YOUR_SERVICE_ROLE_KEY" \\
  "https://YOUR_PROJECT_REF.functions.supabase.co/sync-tcgcsv?category=3"</code></pre>
        <p class="text-[var(--muted)]">Once that's run (takes ~30s), reopen this picker.</p>
      </div>`,
      `<button class="btn btn-ghost" id="_spCancel2">Close</button>`
    );
    document.getElementById("_spCancel2")?.addEventListener("click", closeSealedPicker);
    return;
  }

  await runSearch(seed, setHint);
}

async function runSearch(query: string, setHint: string): Promise<void> {
  let rows: SealedProductRow[] = [];
  try {
    rows = await searchSealedProducts({ query, limit: 60 });
  } catch (e: any) {
    renderSubModal(
      "Pick a sealed product — error",
      `<div class="panel-2 p-4 text-rose-300 text-sm">Lookup failed: ${escapeHtml(e?.message || String(e))}</div>`,
      `<button class="btn btn-ghost" id="_spCancel3">Close</button>`
    );
    document.getElementById("_spCancel3")?.addEventListener("click", closeSealedPicker);
    return;
  }

  // Light client-side ranking: rows whose group_name contains setHint bubble up.
  if (setHint) {
    const sh = setHint.toLowerCase();
    rows.sort((a, b) => {
      const aHit = (a.group_name || "").toLowerCase().includes(sh) ? 1 : 0;
      const bHit = (b.group_name || "").toLowerCase().includes(sh) ? 1 : 0;
      return bHit - aHit;
    });
  }

  _rows = rows;
  renderResults(query, setHint);
}

function renderResults(query: string, setHint: string): void {
  const refineEsc = escapeHtml(query).replace(/'/g, "&#39;");
  const rows = _rows;

  const body = `
    <div class="space-y-3 mb-3">
      <div class="flex flex-wrap gap-2">
        <input type="text" id="sealedPickerRefine" class="input flex-1 min-w-[240px]"
               placeholder='Refine: e.g. "surging sparks etb"'
               value="${refineEsc}">
        <button class="btn btn-primary" id="_spSearch">Search</button>
      </div>
      <div class="text-xs text-[var(--muted)] panel-2 p-2 rounded">
        Showing <b>${rows.length}</b> ${rows.length === 1 ? "match" : "matches"} from the local TCGCSV catalog (synced daily).
        ${setHint ? ` Items from <b>${escapeHtml(setHint)}</b> are sorted to the top.` : ""}
        Prices below are <b>market</b> (or <b>mid</b> when market is missing).
      </div>
    </div>
    ${rows.length ? `
      <div class="grid-cards" id="_spGrid">${rows.map((r, i) => {
        const price = r.market_price ?? r.mid_price ?? r.low_price ?? null;
        const kindBadge = r.product_kind
          ? `<span class="pill pill-violet" style="font-size:.62rem; padding:.05rem .4rem">${escapeHtml(r.product_kind)}</span>`
          : "";
        return `
          <div class="panel-2 p-3 clickable" data-sp-idx="${i}">
            <div class="flex gap-2">
              ${r.image_url ? `<img src="${escapeHtml(r.image_url)}" class="w-16 rounded shrink-0 object-contain" style="background:#fff">` : ""}
              <div class="text-sm min-w-0 flex-1">
                <div class="font-semibold truncate" title="${escapeHtml(r.name)}">${escapeHtml(r.name)}</div>
                <div class="text-xs text-[var(--muted)] truncate" title="${escapeHtml(r.group_name || "")}">${escapeHtml(r.group_name || "—")}</div>
                <div class="flex items-center gap-2 mt-1">
                  ${kindBadge}
                  <span class="text-xs text-emerald-300">${price != null ? fmt$(price) : '<span class="text-[var(--muted)]">no price</span>'}</span>
                </div>
                ${trendButtonHtml(`data-sp-trend="${i}"`)}
              </div>
            </div>
          </div>`;
      }).join("")}</div>` : `
        <div class="text-center text-[var(--muted)] py-6 text-sm">No matches. Try a shorter or different search term.</div>
    `}
  `;
  renderSubModal(
    `Pick a sealed product — ${rows.length} ${rows.length === 1 ? "match" : "matches"}`,
    body,
    `<button class="btn btn-ghost" id="_spCancel4">Cancel</button>`
  );
  document.getElementById("_spCancel4")?.addEventListener("click", closeSealedPicker);
  document.getElementById("_spSearch")?.addEventListener("click", () => {
    const v = (document.getElementById("sealedPickerRefine") as HTMLInputElement).value;
    runSearch(v, setHint);
  });
  document.getElementById("sealedPickerRefine")?.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Enter") {
      e.preventDefault();
      const v = (document.getElementById("sealedPickerRefine") as HTMLInputElement).value;
      runSearch(v, setHint);
    }
  });
  document.getElementById("_spGrid")?.addEventListener("click", (e) => {
    const trendBtn = (e.target as HTMLElement).closest<HTMLElement>("[data-sp-trend]");
    if (trendBtn) {
      const r = _rows[Number(trendBtn.dataset.spTrend)];
      const tile = trendBtn.closest<HTMLElement>("[data-sp-idx]");
      if (r && tile) void toggleCatalogTrend(tile, "sealed", r.product_id);
      return;
    }
    if (isTrendClick(e.target)) return;   // clicks inside an open chart never pick
    const tile = (e.target as HTMLElement).closest<HTMLElement>("[data-sp-idx]");
    if (!tile) return;
    pickSealedProduct(Number(tile.dataset.spIdx));
  });

  setTimeout(() => {
    const ri = document.getElementById("sealedPickerRefine") as HTMLInputElement | null;
    if (ri) { ri.focus(); ri.setSelectionRange(ri.value.length, ri.value.length); }
  }, 50);
}

/** Fill the sealed form with the picked product's data. */
export function pickSealedProduct(i: number): void {
  const r = _rows[i];
  if (!r) { closeSealedPicker(); return; }

  // Generic override: callers (e.g. the Purchase form) can set
  // window._sealedOnPick to receive the picked catalog row instead of having it
  // written into the Sealed form. Mirrors quickCardLookup's _lookupOnPick.
  const onPick = (window as any)._sealedOnPick;
  if (typeof onPick === "function") {
    try { onPick(r); } catch (e) { console.error("[sealed onPick]", e); }
    closeSealedPicker();
    return;
  }

  const form = document.getElementById("sealedForm") as HTMLFormElement | null;
  if (!form) { closeSealedPicker(); return; }

  const setField = (name: string, value: string) => {
    const el = form.querySelector<HTMLInputElement>(`[name="${name}"]`);
    if (el && value != null) el.value = value;
  };

  setField("Product", r.name || "");
  // Set the Game from category_id if we know it (covers every synced game)
  const pickedGame = CATEGORY_TO_GAME[r.category_id];
  if (pickedGame) setField("Game", pickedGame);

  // Set the Set combobox to the group name
  const setInput = form.querySelector<HTMLInputElement>('.combobox-wrapper[data-cbx="Set"] input');
  if (setInput && r.group_name) setInput.value = normalizeSetName(r.group_name);

  // Map TCGCSV product_kind to a Product Type list value if there's a sensible mapping
  if (r.product_kind) {
    const productTypeMap: Record<string, string> = {
      "box":        "Booster Box",
      "etb":        "ETB",
      "bundle":     "Booster Bundle",
      "pack":       "Booster Pack",
      "tin":        "Tin",
      "blister":    "Blister",
      "deck":       "Deck",
      "collection": "Collection Box",
      "other":      "Other",
    };
    const pt = productTypeMap[r.product_kind];
    if (pt) setField("Product Type", pt);
  }

  // Image URL — fill if blank or different
  if (r.image_url) {
    setField("Image URL", r.image_url);
    const imgInput = form.querySelector<HTMLInputElement>('[name="Image URL"]');
    imgInput?.dispatchEvent(new Event("input", { bubbles: true }));
  }

  // Current FMV — prefer market, fall back to mid, then low
  const price = r.market_price ?? r.mid_price ?? r.low_price ?? null;
  if (price != null) {
    const fmvField = form.querySelector<HTMLInputElement>('[name="Current FMV"]');
    if (fmvField) fmvField.value = String(+Number(price).toFixed(2));
    setField("Valuation Source", "TCGplayer Market");
  }

  // Stash the productId on the form via a hidden input so saveSealed can persist
  // it. saveSealed reads form via readForm which walks all named inputs.
  let pidInput = form.querySelector<HTMLInputElement>('input[name="tcgplayer_product_id"]');
  if (!pidInput) {
    pidInput = document.createElement("input");
    pidInput.type  = "hidden";
    pidInput.name  = "tcgplayer_product_id";
    form.appendChild(pidInput);
  }
  pidInput.value = String(r.product_id);

  markModalDirty();
  closeSealedPicker();
  toast(`Filled: ${r.name}${r.group_name ? " · " + r.group_name : ""}`);
}

// Inline-handler bridges (sealed.ts uses these via onclick="…")
(window as any).openSealedPicker  = openSealedPicker;
(window as any).pickSealedProduct = pickSealedProduct;
(window as any).closeSealedPicker = closeSealedPicker;
